import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getAppSocket } from '../lib/wesSocket';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/ui/breadcrumb';
import PaginationFooter from '../components/PaginationFooter';
import { UsersIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  skills?: string[];
  permissions?: string[] | { added?: string[]; removed?: string[] };
  inactiveReason?: string | null;
  inactiveNote?: string | null;
  inactiveAt?: string | null;
}

// Sufijo segun el delta de permisos del usuario respecto del rol:
// '+' suma, '-' quita, '±' ambos.
const rolePermissionSuffix = (permissions: User['permissions']): string => {
  if (!permissions || Array.isArray(permissions)) return '';
  const added = Array.isArray(permissions.added) ? permissions.added.length : 0;
  const removed = Array.isArray(permissions.removed) ? permissions.removed.length : 0;
  if (added > 0 && removed > 0) return ' ±';
  if (added > 0) return ' +';
  if (removed > 0) return ' -';
  return '';
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const fetchUsers = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data.users || []);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Error al cargar los usuarios');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();

    const handleKeyPress = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'n' || e.key === 'N') {
        navigate('/users/new');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const realtimeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/users') || path.startsWith('/api/roles'))) {
        return;
      }
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchUsers({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, []);

  const handleDelete = async (id: string) => {
    if (!await confirmDialog('Eliminar este usuario?')) return;
    try {
      await axios.delete(`/api/users/${id}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar el usuario');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button onClick={() => navigate('/settings')} className="text-left">
                Configuración
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Usuarios</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Usuarios</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/settings')} size="sm">
            Volver
          </Button>
          <Button
            onClick={() => navigate('/users/new')}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            size="sm"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo usuario
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay usuarios registrados</p>
            </div>
          ) : (
            <div className="hidden md:block relative w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Usuario</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Email</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Rol</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                    <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    .slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize)
                    .map((user) => (
                    <TableRow key={user.id} className="border-b hover:bg-muted/50">
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="font-medium">{user.username}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div>{user.email}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left capitalize">
                        {user.role
                          ? <>{user.role}<span className="font-bold not-italic">{rolePermissionSuffix(user.permissions)}</span></>
                          : <span className="text-muted-foreground">Sin rol</span>}
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                        <div className="text-sm">{user.isActive ? 'Activo' : 'Inactivo'}</div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                        <div className="flex items-center justify-end">
                          <ButtonGroup>
                            <Tip label="Editar usuario">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => navigate(`/users/${user.id}/edit`)}
                                className="size-8"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            <Tip label="Eliminar usuario">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleDelete(user.id)}
                                className="size-8"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          </ButtonGroup>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaginationFooter
        currentPage={currentPage}
        totalPages={Math.max(1, Math.ceil(users.length / pageSize))}
        totalResults={users.length}
        startResult={(currentPage - 1) * pageSize + 1}
        onFirst={() => setCurrentPage(1)}
        onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
        onNext={() => setCurrentPage((p) => Math.min(Math.max(1, Math.ceil(users.length / pageSize)), p + 1))}
        onLast={() => setCurrentPage(Math.max(1, Math.ceil(users.length / pageSize)))}
      />
    </div>
  );
};

export default Users;
